import 'regenerator-runtime/runtime';
import { AnyAction } from 'redux';
import { SagaIterator } from 'redux-saga';
import { call, put, select, takeEvery } from 'redux-saga/effects';
import {
  CALLER_ID,
  DoRequestResponse,
  GlobalActions,
  GlobalPut,
  HTTP_METHOD,
  ResponseType,
} from '@ps20-frontend/shop-integration-redux';
import { App } from '@ps20-frontend/frontend-models';
import { safeSaga } from '@ps20-frontend/safe-saga';
import { selectPlzRequiresUwIsAnswered } from '../../app/shared/selectors/plzRequiresUw.selector';
import { IAppState } from '../appState';
import { VwgActions } from '../vwgActions';

const globalActions = new GlobalActions();
const vwgActions = new VwgActions();

export enum PlzRequiresUwCheckEvent {
  PLZ_REQUIRES_UW_CHECK_SUCCESSFUL = 'PLZ_REQUIRES_UW_CHECK_SUCCESSFUL',
  PLZ_REQUIRES_UW_CHECK_ERROR = 'PLZ_REQUIRES_UW_CHECK_ERROR',
}

export function* watchPlzRequiresUwCheck(): SagaIterator {
  yield takeEvery(
    GlobalActions.ADDRESSVORSCHLAG_SUCCESSFUL,
    safeSaga(requestCheckWhenAddressResolved, App.VWG_V2),
  );
  yield takeEvery(GlobalActions.SAVED_STATES_LOADED, safeSaga(requestCheckAfterLoad, App.VWG_V2));
  yield takeEvery(
    GlobalActions.IMMOBILIE_UEBERNEHMEN,
    safeSaga(requestCheckWhenImmobilieUebernommen, App.VWG_V2),
  );
  yield takeEvery(
    GlobalActions.VORBELEGUNG_FOR_BEIBEHALTUNG_REQUESTED,
    safeSaga(requestCheckWhenVorbelegungApplied, App.VWG_V2),
  );
  yield takeEvery(
    VwgActions.PLZ_REQUIRES_UW_CHECK_REQUESTED,
    safeSaga(requestPlzRequiresUwCheck, App.VWG_V2),
  );
  yield takeEvery(
    PlzRequiresUwCheckEvent.PLZ_REQUIRES_UW_CHECK_SUCCESSFUL,
    safeSaga(plzRequiresUwCheckSuccessful, App.VWG_V2),
  );
  yield takeEvery(
    PlzRequiresUwCheckEvent.PLZ_REQUIRES_UW_CHECK_ERROR,
    safeSaga(plzRequiresUwCheckError, App.VWG_V2),
  );
}

const PLZ_PATTERN = /^\d{5}$/;

function isAddressResolved(state: IAppState): boolean {
  return state.adressvorschlag?.lastError === null && state.adressvorschlag?.orte?.length > 0;
}

function isOwnCaller(action: AnyAction, state: IAppState): boolean {
  return action.payload?.callerId === `${CALLER_ID.VWG_BASISDATEN}_${state.angebotsId}`;
}

function isCheckStillMissing(state: IAppState): boolean {
  return PLZ_PATTERN.test(state.risikoort?.plz ?? '') && !selectPlzRequiresUwIsAnswered(state);
}

export function* requestCheckWhenAddressResolved(action: AnyAction): SagaIterator {
  const state: IAppState = yield select();

  if (isOwnCaller(action, state) && isAddressResolved(state) && isCheckStillMissing(state)) {
    yield put(vwgActions.plzRequiresUwCheckRequested(state.angebotsId));
  }
}

export function* requestCheckAfterLoad(): SagaIterator {
  const state: IAppState = yield select();

  if (isCheckStillMissing(state)) {
    yield put(vwgActions.plzRequiresUwCheckRequested(state.angebotsId));
  }
}

function targetsThisAngebot(action: AnyAction, state: IAppState): boolean {
  return (
    action.payload?.appId === App.VWG_V2 && action.payload?.angebotsId === state.angebotsId
  );
}

export function* requestCheckWhenImmobilieUebernommen(action: AnyAction): SagaIterator {
  const state: IAppState = yield select();

  if (targetsThisAngebot(action, state) && isCheckStillMissing(state)) {
    yield put(vwgActions.plzRequiresUwCheckRequested(state.angebotsId));
  }
}

function vorbelegungTargetsThisAngebot(action: AnyAction, state: IAppState): boolean {
  return (
    action.payload?.forApp === App.VWG_V2 && action.payload?.angebotsId === state.angebotsId
  );
}

export function* requestCheckWhenVorbelegungApplied(action: AnyAction): SagaIterator {
  const state: IAppState = yield select();

  if (vorbelegungTargetsThisAngebot(action, state) && isCheckStillMissing(state)) {
    yield put(vwgActions.plzRequiresUwCheckRequested(state.angebotsId));
  }
}

export function* plzRequiresUwCheckSuccessful(
  successAction: DoRequestResponse<{ requiresUw: boolean }>,
): SagaIterator {
  yield put(vwgActions.plzRequiresUwReceived(successAction.payload.requiresUw));
}

export function* plzRequiresUwCheckError(): SagaIterator {
  yield put(vwgActions.plzRequiresUwCheckFailed());
}

export function* requestPlzRequiresUwCheck(): SagaIterator {
  const globalPut = GlobalPut.getInstance();
  const state: IAppState = yield select();

  yield call(
    [globalPut, globalPut.put],
    globalActions.doRequest({
      appId: App.VWG_V2,
      method: HTTP_METHOD.POST,
      retry: {
        delay: 1000,
        times: 3,
      },
      responseType: ResponseType.APPLICATION_JSON,
      body: {
        plz: state.risikoort.plz,
      },
      successEvent: PlzRequiresUwCheckEvent.PLZ_REQUIRES_UW_CHECK_SUCCESSFUL,
      errorEvent: PlzRequiresUwCheckEvent.PLZ_REQUIRES_UW_CHECK_ERROR,
      url: 'privatschutz/api/plzRequiresUw',
    }),
  );
}
